import { err, ok } from 'neverthrow'
import { evaluatorErrorWithNode } from '../../scriptevaluator/EvaluatorError'
import { functionNodeTypes, HelperFunction } from '../FunctionTypes'
import { ConceptStatusValue } from '@fusion-advantage/backcompat'

const _calculateBMI = (bmi: ConceptStatusValue, comorbid: boolean, massivelyOverweight: boolean, cachexic: boolean): {dirId: number|null, interpretation: string}|null => {
	
	if(!bmi) {
		if(cachexic) {
			return { dirId: 1125, interpretation: 'Underweight' }
		}
		else if(massivelyOverweight) {
			return { dirId: 1370, interpretation: 'Severe obesity' }
		}
	}
	
	if (isNaN(Number(bmi))) return null
	const bmiNum = Number(bmi)

	if(bmiNum <= 0) return null

	if(bmiNum < 15) {
		return { dirId: 1125, interpretation: 'Severely underweight' }
	} else if(bmiNum < 18.5) {
		return { dirId: 1125, interpretation: 'Underweight' }
	} else if (bmiNum >= 18.5 && bmiNum < 25) {
		return { dirId: null, interpretation: 'Normal Weight' }
	} else if (bmiNum >= 25 && bmiNum < 30) {
		return { dirId: 1159, interpretation: 'Overweight' }
	} else if (bmiNum >= 30 && bmiNum < 35) {
		return { dirId: 1061, interpretation: 'Obesity' }
	} else if (bmiNum >= 35 && bmiNum < 40 && !comorbid) {
		return { dirId: 1062, interpretation: 'Obesity' }
	} else if (bmiNum >= 35 && bmiNum < 40 && comorbid) {
		return { dirId: 1370, interpretation: 'Severe (morbid) obesity with comorbid condition' }
	} else if (bmiNum >= 40 && comorbid) {
		return { dirId: 1370, interpretation: 'Severe (morbid) obesity with comorbid condition' }
	} else if (bmiNum >= 40 && !comorbid) {
		return { dirId: 1372, interpretation: 'Severe (morbid) obesity with no comorbidities' }
	}

	return null
}

const _isBMI = (dirId: number, bmi: ConceptStatusValue, comorbid: boolean, massivelyOverweight: boolean, cachexic: boolean): boolean =>{
	return _calculateBMI(bmi, comorbid, massivelyOverweight, cachexic)?.dirId === dirId
}

const _bmiInterpretation = (bmi: ConceptStatusValue, comorbid: boolean, massivelyOverweight: boolean, cachexic: boolean): string | null => {
	return _calculateBMI(bmi, comorbid, massivelyOverweight, cachexic)?.interpretation || null
}

export const BMIInterpretationFunction: HelperFunction = {
	tokenName: 'BMI_INTERPRETATION',
	nodeType: functionNodeTypes.BMIInterpretation,
	evaluate(node, evaluateExpr) {
		if (node.arguments.length !== 4) {
			return err(evaluatorErrorWithNode('BMIInterpretation function expects exactly four arguments', node))
		}

		const bmiResult = evaluateExpr(node.arguments[0])
		if (bmiResult.isErr()) return err(bmiResult.error)
		if (bmiResult.value === undefined || Array.isArray(bmiResult.value)) return ok(null)
		const bmi = bmiResult.value

		const comorbidResult = evaluateExpr(node.arguments[1])
		if (comorbidResult.isErr()) return err(comorbidResult.error)
		const comorbid = !!comorbidResult.value

		const massivelyOverweightResult = evaluateExpr(node.arguments[2])
		if (massivelyOverweightResult.isErr()) return err(massivelyOverweightResult.error)
		const massivelyOverweight = !!massivelyOverweightResult.value

		const cachexicResult = evaluateExpr(node.arguments[3])
		if (cachexicResult.isErr()) return err(cachexicResult.error)
		const cachexic = !!cachexicResult.value

		return ok(_bmiInterpretation(bmi, comorbid, massivelyOverweight, cachexic))
	}
}

export const IsBMIFunction: HelperFunction = {
	tokenName: 'IS_BMI',
	nodeType: functionNodeTypes.IsBMI,
	evaluate(node, evaluateExpr) {
		if (node.arguments.length !== 5) {
			return err(evaluatorErrorWithNode('IsBMI function expects exactly five arguments', node))
		}

		const dirIdResult = evaluateExpr(node.arguments[0])
		if (dirIdResult.isErr()) return err(dirIdResult.error)
		if (dirIdResult.value === undefined || Array.isArray(dirIdResult.value)) return ok(null)
		const dirId = Number(dirIdResult.value)

		const bmiResult = evaluateExpr(node.arguments[1])
		if (bmiResult.isErr()) return err(bmiResult.error)
		if (bmiResult.value === undefined || Array.isArray(bmiResult.value)) return ok(null)
		const bmi = bmiResult.value

		const comorbidResult = evaluateExpr(node.arguments[2])
		if (comorbidResult.isErr()) return err(comorbidResult.error)
		const comorbid = !!comorbidResult.value

		const massivelyOverweightResult = evaluateExpr(node.arguments[3])
		if (massivelyOverweightResult.isErr()) return err(massivelyOverweightResult.error)
		const massivelyOverweight = !!massivelyOverweightResult.value

		const cachexicResult = evaluateExpr(node.arguments[4])
		if (cachexicResult.isErr()) return err(cachexicResult.error)
		const cachexic = !!cachexicResult.value

		return ok(_isBMI(dirId, bmi, comorbid, massivelyOverweight, cachexic))
	}
}